import type { ReviewLevel, ReviewSortKey } from '@/client'

/**
 * Which sort keys the Review AMS backend accepts at which level — VentaHub
 * `pages/review_ams_data/sortKeys.ts`, verbatim.
 *
 * Most sort keys (the metric columns and the entity name) exist at every
 * level, so the page keeps the reader's sort across tabs. A few only exist on
 * one level — `bid` comes from the target aggregate's live-master lookup — and
 * asking for it elsewhere is refused. Level changes therefore go through
 * `resolveSortForLevel`, which falls back to the default when the current key
 * would not survive the move.
 */

/** Sort keys valid at exactly one level, mapped to the level that owns them (sortKeys.ts:17-19). */
export const LEVEL_ONLY_SORT_KEYS: Partial<Record<ReviewSortKey, ReviewLevel>> = {
  bid: 'target',
}

export const DEFAULT_SORT_BY: ReviewSortKey = 'curr_spend'
export const DEFAULT_SORT_DIR: SortDir = 'desc'

/**
 * The only sort keys `level=alt_product` accepts (sortKeys.ts:32-41): the
 * purchased-product report carries no impressions, clicks or spend, so only
 * the reduced metric set, the entity name and the ad-group count are legal.
 */
export const ALT_PRODUCT_SORT_KEYS: ReadonlySet<ReviewSortKey> = new Set<ReviewSortKey>([
  'name',
  'ad_group_count',
  'curr_units',
  'curr_sales',
  'curr_asp',
  'prev_units',
  'prev_sales',
  'prev_asp',
])

export type SortDir = 'asc' | 'desc'

export type SortState = { sortBy: ReviewSortKey; sortDir: SortDir }

/** Sales lead the halo / cross-sell read, so Alt Products defaults to them (sortKeys.ts:43-44). */
export const ALT_PRODUCT_DEFAULT_SORT: SortState = { sortBy: 'curr_sales', sortDir: 'desc' }

/** The sort a level opens with when nothing else was chosen. */
export function defaultSortFor(level: ReviewLevel): SortState {
  return level === 'alt_product'
    ? ALT_PRODUCT_DEFAULT_SORT
    : { sortBy: DEFAULT_SORT_BY, sortDir: DEFAULT_SORT_DIR }
}

/**
 * The sort to apply after moving to `toLevel` (sortKeys.ts:60-72): the current
 * one — the same object — when it is legal there; otherwise the level's
 * default, `curr_sales desc` for Alt Products and `curr_spend desc` elsewhere.
 * A key no rule mentions is assumed to exist on every level.
 */
export function resolveSortForLevel(current: SortState, toLevel: ReviewLevel): SortState {
  if (toLevel === 'alt_product') {
    return ALT_PRODUCT_SORT_KEYS.has(current.sortBy) ? current : ALT_PRODUCT_DEFAULT_SORT
  }
  const owner = LEVEL_ONLY_SORT_KEYS[current.sortBy]
  if (owner && owner !== toLevel) {
    return { sortBy: DEFAULT_SORT_BY, sortDir: DEFAULT_SORT_DIR }
  }
  return current
}
